import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Paper,
  Stack,
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from "@mui/material";

import { useState, useMemo } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { offlineQueue } from './services/offlineQueue';
import { formatearFechaLocal, normalizarFechaISO } from '../../utils/horaLocal';
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { ModalGastos } from './ModalesInventario';

export default function HistorialGastos({
  state,
  gastos,
  categorias,
  registrarGasto,
  editarGasto,
  anularGasto,
  agregarCategoria,
  onConvertirALimpieza,
  onAfterSync,
}) {

  const [filtro, setFiltro] = useState("TODOS");
  const [open, setOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [done, setDone] = useState(false);

  // Verificar si el día está bloqueado por arqueo
  const díaBloqueado = state?.verificarDíaBloqueado();
  const arqueoDelDía = state?.obtenerArqueoDelDía();

  const handleSync = async () => {
    setSyncing(true);
    setDone(false);
    
    // Ejecuta la sincronización masiva de todas las keys
    await offlineQueue.flush();
    await onAfterSync?.();

    setSyncing(false);
    setDone(true);

    // Feedback visual temporal
    setTimeout(() => setDone(false), 2000);
  };


  const handleOpen = () => {
    setOpen(true);
    setEditando(null);
  };


  const handleClose = () => {
    setOpen(false);
    setEditando(null);
  };


  const handleEditar = (g) => {
    setEditando(g);
    setOpen(true);
  };


  const handleRegistrarGastos = async (gastosArray) => {
    // gastosArray viene del ModalGastos con todos los gastos registrados
    await handleSync();
    handleClose();
  };


  const guardarEdicion = async () => {
    // Verificar si el día está bloqueado
    if (díaBloqueado) {
      alert('⛔ No puedes editar gastos cuando hay un arqueo completado en el día. Anula el arqueo para editar.');
      return;
    }

    await editarGasto(
      editando.id,
      editando.monto,
      editando.categoriaId,
      editando.descripcion,
      editando.metodoPago || 'EFECTIVO'
    );

    // Espera a que React ejecute los useEffect de persistencia
    await new Promise(resolve => setTimeout(resolve, 0));
    await handleSync();
    handleClose();
  };


  const lista = useMemo(() => {
    const base =
      filtro === "TODOS"
        ? gastos
        : gastos.filter(g => g.categoriaId === filtro);

    return [...base].sort(
      (a, b) => new Date(b.fecha) - new Date(a.fecha)
    );
  }, [gastos, filtro]);


  return (
    <Paper sx={{ p: 3, borderRadius: 3 }}>

      <Stack direction="row" justifyContent="space-between" mb={4}>

        <Box>

          <Typography variant="h4" fontWeight="bold">
            Control de Gastos
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Registro operativo
          </Typography>

        </Box>

        <Stack direction="row" spacing={2}>

          <Button
            variant="contained"
            color="error"
            onClick={handleOpen}
            disabled={díaBloqueado}
          >
            + Gasto
          </Button>

          <Button
            variant="outlined"
            onClick={() => {

              const n = prompt("Nombre categoría");

              if (n) agregarCategoria(n);

            }}
            disabled={díaBloqueado}
          >
            Nueva Categoría
          </Button>

        </Stack>

      </Stack>

      {/* ADVERTENCIA DE DÍA BLOQUEADO */}
      {díaBloqueado && (
        <Box sx={{ 
          bgcolor: '#ffebee', 
          p: 2, 
          borderRadius: 2, 
          border: '2px solid #f44336',
          mb: 3
        }}>
          <Stack spacing={1}>
            <Typography sx={{ fontWeight: 'bold', color: '#d32f2f', fontSize: '1.1rem' }}>
              🔒 DÍA BLOQUEADO - ARQUEO DE CAJA COMPLETADO
            </Typography>
            <Typography variant="body2" sx={{ color: '#c62828' }}>
              No puedes agregar nuevos gastos. El arqueo de caja fue completado hoy a las {new Date(arqueoDelDía.fecha).toLocaleTimeString('es-BO')}.
            </Typography>
            <Typography variant="body2" sx={{ color: '#c62828', fontStyle: 'italic' }}>
              Para desbloquear, ve a la pestaña "Arqueo de Caja" y anula el arqueo de hoy.
            </Typography>
          </Stack>
        </Box>
      )}


      <Select
        size="small"
        value={filtro}
        onChange={e => setFiltro(e.target.value)}
      >

        <MenuItem value="TODOS">
          Todas las categorías
        </MenuItem>

        {categorias.map(c => (
          <MenuItem key={c.id} value={c.id}>
            {c.nombre}
          </MenuItem>
        ))}

      </Select>


      <Box sx={{ overflowX: "auto", mt: 3 }}>

        <Table size="small">

          <TableHead>

            <TableRow>

              <TableCell>Fecha</TableCell>
              <TableCell>Categoría</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell align="right">Monto</TableCell>
              <TableCell align="center">Método</TableCell>
              <TableCell align="center">Estado</TableCell>
              <TableCell align="center">Acciones</TableCell>

            </TableRow>

          </TableHead>

          <TableBody>

            {lista.map(g => (

              <TableRow
                key={g.id}
                sx={{
                  opacity: g.estado === "ANULADO" ? 0.4 : 1
                }}
              >

                <TableCell>
                  {formatearFechaLocal(normalizarFechaISO(g.fecha))}
                </TableCell>

                <TableCell>

                  <Chip
                    label={
                      categorias.find(c => c.id === g.categoriaId)?.nombre
                    }
                    size="small"
                    color="error"
                  />

                </TableCell>

                <TableCell>{g.descripcion}</TableCell>

                <TableCell align="right">
                  <strong>{g.monto.toFixed(2)}</strong>
                </TableCell>

                <TableCell align="center">
                  <Chip 
                    label={g.metodoPago || 'EFECTIVO'} 
                    size="small"
                    color={g.metodoPago === 'QR' ? 'info' : 'default'}
                  />
                </TableCell>

                <TableCell align="center">

                  {g.estado === "ANULADO" && (
                    <Chip label="ANULADO" />
                  )}

                  {g.pendingSync && (
                    <Chip label="Pendiente sync" size="small" color="warning" variant="outlined" sx={{ ml: 0.5 }} />
                  )}

                  {g.estado !== "ANULADO" && (
                    <Chip label="Activo" color="success" />
                  )}

                </TableCell>

                <TableCell align="center">

                  {g.estado !== "ANULADO" && (

                    <>

                      <IconButton
                        onClick={() => handleEditar(g)}
                        disabled={díaBloqueado}
                      >
                        <EditIcon />
                      </IconButton>

                      <Button
                        size="small"
                        color="secondary"
                        disabled={díaBloqueado}
                        onClick={() => anularGasto(g.id)}
                      >
                        Anular
                      </Button>

                      {onConvertirALimpieza && !g.convertidoEnLimpiezaId && (
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ ml: 1 }}
                          onClick={() => onConvertirALimpieza(g)}
                        >
                          Convertir
                        </Button>
                      )}

                    </>

                  )}

                  {g.convertidoEnLimpiezaId && (
                    <Chip
                      size="small"
                      label={`Convertido a limpieza`}
                      color="info"
                      sx={{ mt: 0.5 }}
                    />
                  )}

                </TableCell>

              </TableRow>

            ))}

          </TableBody>

        </Table>

      </Box>




      {editando ? (
        // Dialog simple para editar un gasto existente
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
          <DialogTitle>Editar gasto</DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Stack spacing={2}>
              <Select
                fullWidth
                label="Categoría"
                value={editando.categoriaId}
                onChange={e => setEditando({...editando, categoriaId: e.target.value})}
              >
                <MenuItem value="" disabled>Categoría</MenuItem>
                {categorias.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.nombre}</MenuItem>
                ))}
              </Select>
              <TextField
                fullWidth
                label="Monto"
                type="number"
                value={editando.monto}
                onChange={e => setEditando({...editando, monto: e.target.value})}
              />
              <TextField
                fullWidth
                label="Descripción"
                value={editando.descripcion}
                onChange={e => setEditando({...editando, descripcion: e.target.value})}
              />
              <Select
                fullWidth
                value={editando.metodoPago || 'EFECTIVO'}
                onChange={e => setEditando({...editando, metodoPago: e.target.value})}
              >
                <MenuItem value="EFECTIVO">Efectivo</MenuItem>
                <MenuItem value="QR">QR</MenuItem>
              </Select>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancelar</Button>
            <Button onClick={guardarEdicion} variant="contained">Guardar cambios</Button>
          </DialogActions>
        </Dialog>
      ) : (
        // Modal para registrar nuevos gastos
        <ModalGastos
          open={open}
          onClose={handleClose}
          state={state}
          gastosState={{ registrarGasto }}
          categorias={categorias}
          díaBloqueado={díaBloqueado}
          onRegistrar={handleRegistrarGastos}
        />
      )}

      {/* ================= LOADER GLOBAL ================= */}
      <Backdrop
        open={syncing}
        sx={{
          color: '#fff',
          zIndex: theme => theme.zIndex.drawer + 999,
          flexDirection: 'column'
        }}
      >
        <CircularProgress color="inherit" size={60} />
        <Typography mt={2} fontWeight="bold">
          Sincronizando información…
        </Typography>
      </Backdrop>

      {/* ================= FEEDBACK OK ================= */}
      {done && (
        <Backdrop
          open
          sx={{
            color: '#fff',
            zIndex: theme => theme.zIndex.drawer + 999,
            flexDirection: 'column',
            bgcolor: 'rgba(0,0,0,0.6)'
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main' }} />
          <Typography mt={2} fontWeight="bold">
            Información sincronizada
          </Typography>
        </Backdrop>
      )}

    </Paper>
  );

}